import React, { useState, useEffect } from 'react';
import { journeyAPI } from '../services/api';
import { Journey, JourneyStatus } from '../types';
import JourneyMap from '../components/JourneyMap';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Collapse,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge
} from '@mui/material';
import { 
  Refresh, 
  FilterList, 
  LocationOn, 
  DirectionsTransit,
  CheckCircle,
  PlayArrow,
  Stop,
  Visibility,
  Timeline,
  AddCircle,
  Train,
  SignalCellularOff,
  Timer,
  Cancel,
  Error
} from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const Journeys: React.FC = () => {
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState<Journey | null>(null);
  const [detailsTab, setDetailsTab] = useState(0);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filterForm, setFilterForm] = useState({
    status: '',
    userId: '',
    sourceStation: '',
    destinationStation: '',
    dateFrom: '',
    dateTo: ''
  });
  
  const [successMsg, setSuccessMsg] = useState<string>('');

  useEffect(() => {
    fetchJourneys();
  }, []);

  const fetchJourneys = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await journeyAPI.getAll();
      
      // Debug: Log the response structure
      console.log('Journey API Response:', res);
      console.log('Response type:', typeof res);
      console.log('Response data type:', typeof res?.data);
      console.log('Response data:', res?.data);
      
      // Validate response structure
      if (!res) {
        throw 'No response received from server';
      }
      
      // Handle different response structures
      let journeysData: any[] = [];
      
      if (res.data && Array.isArray(res.data)) {
        // Standard response structure
        journeysData = res.data;
      } else if (Array.isArray(res)) {
        // Direct array response
        journeysData = res;
      } else if (res.data && typeof res.data === 'object' && res.data !== null) {
        // Check for paginated response structures
        const dataObj = res.data as any;
        if (dataObj.content && Array.isArray(dataObj.content)) {
          journeysData = dataObj.content;
        } else if (dataObj.items && Array.isArray(dataObj.items)) {
          journeysData = dataObj.items;
        } else {
          console.warn('Unexpected response structure:', res);
          setJourneys([]);
          return;
        }
      } else {
        console.warn('Unexpected response structure:', res);
        setJourneys([]);
        return;
      }
      
      // Ensure intermediateStations is always an array for each journey
      const journeysWithArrays = journeysData.map(journey => {
        if (!journey || typeof journey !== 'object') {
          console.warn('Invalid journey object:', journey);
          return null;
        }
        
        return {
          ...journey,
          intermediateStations: Array.isArray(journey.intermediateStations) ? journey.intermediateStations : []
        };
      }).filter(Boolean); // Remove null entries
      
      console.log('Processed journeys:', journeysWithArrays);
      setJourneys(journeysWithArrays);
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Error fetching journeys:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data
      });
      setError(error.response?.data?.message || error.message || 'Failed to fetch journeys');
      setJourneys([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Test function to simulate different API response structures
  const testWithMockData = () => {
    setLoading(true);
    setError(null);
    
    // Simulate API delay
    setTimeout(() => {
      try {
        // Test different response structures
        const mockResponses = [
          // Standard response structure
          { data: [
            {
              id: '1',
              journeyName: 'Test Journey 1',
              userId: 'user123',
              sourceStationName: 'Station A',
              destinationStationName: 'Station B',
              status: 'IN_TRANSIT',
              startTime: new Date().toISOString(),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              sourceStationCode: 'STA',
              sourceStationLineId: 'line1',
              sourceStationLineName: 'Blue Line',
              sourceStationLineColor: '#3498db',
              sourceStationLatitude: 12.9716,
              sourceStationLongitude: 77.5946,
              destinationStationCode: 'STB',
              destinationStationLineId: 'line1',
              destinationStationLineName: 'Blue Line',
              destinationStationLineColor: '#3498db',
              destinationStationLatitude: 12.9789,
              destinationStationLongitude: 77.5917,
              intermediateStations: []
            }
          ]},
          // Direct array response
          [
            {
              id: '2',
              journeyName: 'Test Journey 2',
              userId: 'user456',
              sourceStationName: 'Station C',
              destinationStationName: 'Station D',
              status: 'COMPLETED',
              startTime: new Date(Date.now() - 86400000).toISOString(),
              endTime: new Date().toISOString(),
              createdAt: new Date(Date.now() - 86400000).toISOString(),
              updatedAt: new Date().toISOString(),
              sourceStationCode: 'STA',
              sourceStationLineId: 'line1',
              sourceStationLineName: 'Blue Line',
              sourceStationLineColor: '#3498db',
              sourceStationLatitude: 12.9716,
              sourceStationLongitude: 77.5946,
              destinationStationCode: 'STB',
              destinationStationLineId: 'line1',
              destinationStationLineName: 'Blue Line',
              destinationStationLineColor: '#3498db',
              destinationStationLatitude: 12.9789,
              destinationStationLongitude: 77.5917,
              intermediateStations: []
            }
          ],
          // Paginated response structure
          { data: { content: [
            {
              id: '3',
              journeyName: 'Test Journey 3',
              userId: 'user789',
              sourceStationName: 'Station E',
              destinationStationName: 'Station F',
              status: 'STOPPED',
              startTime: new Date(Date.now() - 172800000).toISOString(),
              createdAt: new Date(Date.now() - 172800000).toISOString(),
              updatedAt: new Date().toISOString(),
              sourceStationCode: 'STA',
              sourceStationLineId: 'line1',
              sourceStationLineName: 'Blue Line',
              sourceStationLineColor: '#3498db',
              sourceStationLatitude: 12.9716,
              sourceStationLongitude: 77.5946,
              destinationStationCode: 'STB',
              destinationStationLineId: 'line1',
              destinationStationLineName: 'Blue Line',
              destinationStationLineColor: '#3498db',
              destinationStationLatitude: 12.9789,
              destinationStationLongitude: 77.5917,
              intermediateStations: []
            }
          ]}}
        ];
        
        // Randomly select a response structure
        const randomResponse = mockResponses[Math.floor(Math.random() * mockResponses.length)];
        console.log('Testing with mock response structure:', randomResponse);
        
        // Process the mock response using the same logic
        let journeysData: any[] = [];
        
        if ('data' in randomResponse && randomResponse.data && Array.isArray(randomResponse.data)) {
          journeysData = randomResponse.data;
        } else if (Array.isArray(randomResponse)) {
          journeysData = randomResponse;
        } else if ('data' in randomResponse && randomResponse.data && typeof randomResponse.data === 'object' && randomResponse.data !== null) {
          const dataObj = randomResponse.data as any;
          if (dataObj.content && Array.isArray(dataObj.content)) {
            journeysData = dataObj.content;
          } else if (dataObj.items && Array.isArray(dataObj.items)) {
            journeysData = dataObj.items;
          }
        }
        
        const journeysWithArrays = journeysData.map(journey => ({
          ...journey,
          intermediateStations: Array.isArray(journey.intermediateStations) ? journey.intermediateStations : []
        }));
        
        setJourneys(journeysWithArrays);
        setSuccessMsg('Test data loaded successfully!');
      } catch (error) {
        console.error('Error with test data:', error);
        setError('Failed to load test data');
      } finally {
        setLoading(false);
      }
    }, 1000);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilterForm(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    // Apply filters to journeys
    let filteredJourneys = journeys;
    
    if (filterForm.status) {
      filteredJourneys = filteredJourneys.filter(j => j.status === filterForm.status);
    }
    if (filterForm.userId) {
      filteredJourneys = filteredJourneys.filter(j => j.userId.includes(filterForm.userId));
    }
    if (filterForm.sourceStation) {
      filteredJourneys = filteredJourneys.filter(j => 
        j.sourceStationName.toLowerCase().includes(filterForm.sourceStation.toLowerCase())
      );
    }
    if (filterForm.destinationStation) {
      filteredJourneys = filteredJourneys.filter(j => 
        j.destinationStationName?.toLowerCase().includes(filterForm.destinationStation.toLowerCase())
      );
    }
    if (filterForm.dateFrom) {
      filteredJourneys = filteredJourneys.filter(j => 
        j.createdAt && new Date(j.createdAt) >= new Date(filterForm.dateFrom)
      );
    }
    if (filterForm.dateTo) {
      filteredJourneys = filteredJourneys.filter(j => 
        j.createdAt && new Date(j.createdAt) <= new Date(filterForm.dateTo)
      );
    }
    
    setJourneys(filteredJourneys);
  };

  const clearFilters = () => {
    setFilterForm({
      status: '',
      userId: '',
      sourceStation: '',
      destinationStation: '',
      dateFrom: '',
      dateTo: ''
    });
    fetchJourneys();
  };

  const handleViewDetails = (journey: Journey) => {
    // Ensure intermediateStations is always an array
    const journeyWithArrays = {
      ...journey,
      intermediateStations: Array.isArray(journey.intermediateStations) ? journey.intermediateStations : []
    };
    setSelectedJourney(journeyWithArrays);
    setShowDetailsModal(true);
    setDetailsTab(0);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedJourney(null);
  };

  const getStatusColor = (status: JourneyStatus | string) => {
    switch (status) {
      case 'CREATED':
        return 'default';
      case 'IN_TRANSIT':
        return 'success';
      case 'IN_TRANSIT_BAD_SIGNAL':
        return 'warning';
      case 'ARRIVED':
        return 'primary';
      case 'COMPLETED':
        return 'primary';
      case 'CANCELLED':
        return 'error';
      case 'STOPPED':
        return 'error';
      case 'ERROR':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: JourneyStatus | string) => {
    switch (status) {
      case 'CREATED':
        return <AddCircle />;
      case 'IN_TRANSIT':
        return <Train />;
      case 'IN_TRANSIT_BAD_SIGNAL':
        return <SignalCellularOff />;
      case 'ARRIVED':
        return <Timer />;
      case 'COMPLETED':
        return <CheckCircle />;
      case 'CANCELLED':
        return <Cancel />;
      case 'STOPPED':
        return <Stop />;
      case 'ERROR':
        return <Error />;
      default:
        return <Timeline />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const calculateDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diff = end.getTime() - start.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Journey Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={fetchJourneys}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={testWithMockData}
            disabled={loading}
          >
            Test with Mock Data
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <FilterList sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            <Button
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              sx={{ ml: 'auto' }}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </Box>
          
          <Collapse in={showFilters}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterForm.status}
                    label="Status"
                    onChange={(e: SelectChangeEvent) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CREATED">Created</MenuItem>
                    <MenuItem value="IN_TRANSIT">In Transit</MenuItem>
                    <MenuItem value="IN_TRANSIT_BAD_SIGNAL">In Transit (Bad Signal)</MenuItem>
                    <MenuItem value="ARRIVED">Arrived</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    <MenuItem value="STOPPED">Stopped</MenuItem>
                    <MenuItem value="ERROR">Error</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="User ID"
                  value={filterForm.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Source Station"
                  value={filterForm.sourceStation}
                  onChange={(e) => handleFilterChange('sourceStation', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Destination Station"
                  value={filterForm.destinationStation}
                  onChange={(e) => handleFilterChange('destinationStation', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date From"
                  type="date"
                  value={filterForm.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date To"
                  type="date"
                  value={filterForm.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box display="flex" gap={1}>
                  <Button variant="contained" onClick={applyFilters}>
                    Apply Filters
                  </Button>
                  <Button variant="outlined" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Journeys Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Journey Name</TableCell>
              <TableCell>User ID</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Destination</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {journeys.length > 0 ? (
              journeys.map((journey) => (
                <TableRow key={journey.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {journey.journeyName || 'Unnamed Journey'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {journey.userId ? `${journey.userId.substring(0, 8)}...` : 'Unknown User'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <LocationOn sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {journey.sourceStationName || 'Unknown Station'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <LocationOn sx={{ fontSize: 16, mr: 1, color: 'secondary.main' }} />
                      <Typography variant="body2">
                        {journey.destinationStationName || 'Unknown Station'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(journey.status || 'UNKNOWN')}
                      label={journey.status || 'UNKNOWN'}
                      color={getStatusColor(journey.status || 'UNKNOWN') as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {journey.startTime ? formatDate(journey.startTime) : 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {journey.startTime ? calculateDuration(journey.startTime, journey.endTime) : 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(journey)}
                          color="primary"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box py={3}>
                    <Typography variant="body1" color="textSecondary">
                      {loading ? 'Loading journeys...' : 'No journeys found'}
                    </Typography>
                    {!loading && (
                      <Button
                        variant="outlined"
                        onClick={fetchJourneys}
                        sx={{ mt: 1 }}
                      >
                        Retry
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Journey Details Modal */}
      <Dialog
        open={showDetailsModal}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Journey Details: {selectedJourney?.journeyName}
            </Typography>
            <Chip
              icon={selectedJourney ? getStatusIcon(selectedJourney.status) : undefined}
              label={selectedJourney?.status}
              color={selectedJourney ? getStatusColor(selectedJourney.status) as any : 'default'}
            />
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedJourney && (
            <Box>
              <Tabs value={detailsTab} onChange={(_, newValue) => setDetailsTab(newValue)} sx={{ mb: 2 }}>
                <Tab label="Overview" />
                <Tab label="Route Details" />
                <Tab label="Live Tracking" />
              </Tabs>

              {detailsTab === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Source Station</Typography>
                        <Typography variant="body1">{selectedJourney.sourceStationName}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Lat: {selectedJourney.sourceStationLatitude}, Lng: {selectedJourney.sourceStationLongitude}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Destination Station</Typography>
                        <Typography variant="body1">{selectedJourney.destinationStationName}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Lat: {selectedJourney.sourceStationLatitude}, Lng: {selectedJourney.sourceStationLongitude}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Journey Info</Typography>
                        <Typography variant="body2">
                          <strong>Start Time:</strong> {selectedJourney.startTime ? formatDate(selectedJourney.startTime) : 'N/A'}
                        </Typography>
                        {selectedJourney.endTime && (
                          <Typography variant="body2">
                            <strong>End Time:</strong> {formatDate(selectedJourney.endTime)}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          <strong>Duration:</strong> {calculateDuration(selectedJourney.startTime || '', selectedJourney.endTime)}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Created:</strong> {selectedJourney.createdAt ? formatDate(selectedJourney.createdAt) : 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Current Location</Typography>
                        {selectedJourney.currentLatitude && selectedJourney.currentLongitude ? (
                          <>
                            <Typography variant="body2">
                              <strong>Lat:</strong> {selectedJourney.currentLatitude}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Lng:</strong> {selectedJourney.currentLongitude}
                            </Typography>
                            {selectedJourney.lastLocationUpdate && (
                              <Typography variant="body2" color="textSecondary">
                                Last updated: {formatDate(selectedJourney.lastLocationUpdate)}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No location data available
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {detailsTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Intermediate Stations</Typography>
                  {selectedJourney.intermediateStations && Array.isArray(selectedJourney.intermediateStations) && selectedJourney.intermediateStations.length > 0 ? (
                    <List>
                      {(() => {
                        try {
                          return selectedJourney.intermediateStations
                            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                            .map((station, index) => (
                              <ListItem key={station.stationId || index}>
                                <ListItemIcon>
                                  <Badge badgeContent={station.sequence || 'N/A'} color="primary">
                                    <Avatar sx={{ bgcolor: station.isCrossed ? 'success.main' : 'grey.300' }}>
                                      {station.isCrossed ? <CheckCircle /> : <DirectionsTransit />}
                                    </Avatar>
                                  </Badge>
                                </ListItemIcon>
                                <ListItemText
                                  primary={station.stationName || 'Unknown Station'}
                                  secondary={
                                    <Box>
                                      <Typography variant="body2" color="textSecondary">
                                        Line: {station.lineName || 'Unknown Line'}
                                      </Typography>
                                      <Typography variant="body2" color="textSecondary">
                                        Coordinates: {station.lat || 'N/A'}, {station.lng || 'N/A'}
                                      </Typography>
                                      {station.isCrossed && station.crossedAt && (
                                        <Typography variant="body2" color="success.main">
                                          Crossed at: {formatDate(station.crossedAt)}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ));
                        } catch (error) {
                          console.error('Error rendering intermediate stations:', error);
                          return (
                            <Typography variant="body2" color="error">
                              Error loading intermediate stations. Please try again.
                            </Typography>
                          );
                        }
                      })()}
                    </List>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No intermediate stations defined
                    </Typography>
                  )}
                </Box>
              )}

              {detailsTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Live Tracking</Typography>
                  
                  {/* Journey Map */}
                  <Box mb={3}>
                    <JourneyMap journey={selectedJourney} />
                  </Box>
                  
                  {/* Tracking Information */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Tracking Status</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Current Status: {selectedJourney.status}
                          </Typography>
                          {['IN_TRANSIT', 'IN_TRANSIT_BAD_SIGNAL', 'ARRIVED'].includes(selectedJourney.status) && (
                            <Box mt={2}>
                              <Typography variant="body2" color="success.main">
                                Journey is currently active and being tracked
                              </Typography>
                              {selectedJourney.currentLatitude && selectedJourney.currentLongitude && (
                                <Box mt={1}>
                                  <Typography variant="body2">
                                    Current Position: {selectedJourney.currentLatitude}, {selectedJourney.currentLongitude}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Progress</Typography>
                          {selectedJourney.intermediateStations && Array.isArray(selectedJourney.intermediateStations) && selectedJourney.intermediateStations.length > 0 ? (
                            <Box>
                              {(() => {
                                try {
                                  const crossedCount = selectedJourney.intermediateStations.filter(s => s.isCrossed).length;
                                  const totalCount = selectedJourney.intermediateStations.length;
                                  const progress = totalCount > 0 ? Math.round((crossedCount / totalCount) * 100) : 0;
                                  
                                  return (
                                    <>
                                      <Typography variant="body2">
                                        Stations Crossed: {crossedCount} / {totalCount}
                                      </Typography>
                                      <Typography variant="body2" color="textSecondary">
                                        Progress: {progress}%
                                      </Typography>
                                    </>
                                  );
                                } catch (error) {
                                  console.error('Error calculating progress:', error);
                                  return (
                                    <Typography variant="body2" color="error">
                                      Error calculating progress
                                    </Typography>
                                  );
                                }
                              })()}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No intermediate stations to track
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Journeys;
